import { useReducer, useRef, useCallback } from 'react';
import { Socket } from 'socket.io-client';
import {
  SimulationState,
  SimulationAction,
  ArtifactSnapshot,
  SubmitTurnPayload,
} from '../types/simulation.types';

const initialState: SimulationState = {
  sessionId: null,
  simSessionId: null,
  sessionStatus: 'loading',
  blueprintId: null,
  attemptNumber: null,
  narrativeContext: null,
  artifacts: [],
  turnIndex: 0,
  phase: 1,
  indicators: {},
  actionsDetected: [],
  timeline: [],
  actionText: '',
  reflectionText: '',
  isSubmitting: false,
  mutationEvent: null,
  showMutationOverlay: false,
  timeWarning: null,
  sessionEnd: null,
  turnRejected: null,
  resumeDeadline: null,
  isConnected: false,
  isReconnecting: false,
  error: null,
};

function simulationReducer(
  state: SimulationState,
  action: SimulationAction,
): SimulationState {
  switch (action.type) {
    case 'SET_STATUS':
      return { ...state, sessionStatus: action.payload };

    case 'SESSION_CREATED':
      return {
        ...state,
        sessionId: action.payload.sessionId,
        simSessionId: action.payload.simSessionId,
        blueprintId: action.payload.blueprintId ?? null,
        sessionStatus: 'pending',
      };

    case 'SCENARIO_LOADED':
      return {
        ...state,
        narrativeContext: action.payload.narrativeContext,
        artifacts: action.payload.artifacts,
        sessionStatus: 'scenarioReady',
      };

    case 'STATE_UPDATE':
      return {
        ...state,
        turnIndex: action.payload.turnIndex,
        phase: action.payload.phase,
        indicators: action.payload.indicators,
        actionsDetected: action.payload.actionsDetected,
        timeline: [...state.timeline, action.payload.timelineEntry],
        isSubmitting: false,
        actionText: '',
        reflectionText: '',
        turnRejected: null,
      };

    case 'MUTATION_EVENT':
      return {
        ...state,
        mutationEvent: action.payload,
        showMutationOverlay: true,
        phase: action.payload.phase,
        artifacts: action.payload.injectedArtifacts
          ? [...state.artifacts, ...action.payload.injectedArtifacts]
          : state.artifacts,
      };

    case 'SESSION_END':
      return {
        ...state,
        sessionEnd: action.payload,
        sessionStatus: 'completed',
        isSubmitting: false,
      };

    case 'TURN_REJECTED':
      return {
        ...state,
        turnRejected: action.payload,
        isSubmitting: false,
      };

    case 'TIME_WARNING':
      return {
        ...state,
        timeWarning: action.payload,
      };

    case 'STATE_SNAPSHOT':
      return {
        ...state,
        turnIndex: action.payload.turnIndex,
        phase: action.payload.phase,
        indicators: action.payload.indicators,
        timeline: action.payload.timeline,
        narrativeContext: action.payload.scenario.narrativeContext,
        artifacts: action.payload.scenario.artifacts,
        sessionStatus: 'active',
        isReconnecting: false,
      };

    case 'SESSION_PAUSED':
      return {
        ...state,
        sessionStatus: 'paused',
        resumeDeadline: action.payload.resumeDeadline,
        isSubmitting: false,
      };

    case 'AUTH_ERROR':
      return {
        ...state,
        error: action.payload.message,
        isConnected: false,
      };

    case 'SET_CONNECTED':
      return { ...state, isConnected: action.payload };

    case 'SET_RECONNECTING':
      return { ...state, isReconnecting: action.payload };

    case 'SET_SUBMITTING':
      return { ...state, isSubmitting: action.payload };

    case 'SET_ACTION_TEXT':
      return { ...state, actionText: action.payload };

    case 'SET_REFLECTION_TEXT':
      return { ...state, reflectionText: action.payload };

    case 'SET_ERROR':
      return { ...state, error: action.payload };

    case 'DISMISS_MUTATION_OVERLAY':
      return { ...state, showMutationOverlay: false };

    case 'UPDATE_ARTIFACT':
      return {
        ...state,
        artifacts: state.artifacts.map((a) =>
          a.artifactId === action.payload.artifactId
            ? { ...a, content: action.payload.content }
            : a,
        ),
      };

    case 'RESET':
      return initialState;

    default:
      return state;
  }
}

export function useSimulationState() {
  const [state, dispatch] = useReducer(simulationReducer, initialState);
  const socketRef = useRef<Socket | null>(null);

  const submitTurn = useCallback(
    (
      text: string,
      reflection: string,
      artifactsBefore: ArtifactSnapshot[],
      artifactsAfter: ArtifactSnapshot[],
      artifactReadCount: number,
      artifactWriteCount: number,
    ) => {
      if (!socketRef.current?.connected || !state.simSessionId) return;

      dispatch({ type: 'SET_SUBMITTING', payload: true });

      const payload: SubmitTurnPayload = {
        simSessionId: state.simSessionId,
        text,
        reflection,
        artifactsBefore,
        artifactsAfter,
        artifactReadCount,
        artifactWriteCount,
      };

      socketRef.current.emit('SUBMIT_TURN', payload);
    },
    [state.simSessionId],
  );

  return {
    ...state,
    dispatch,
    socketRef,
    submitTurn,
  };
}

export type SimulationStateReturn = ReturnType<typeof useSimulationState>;
